import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import usePageMeta from "@/hooks/usePageMeta";

const SCOPE = [
  {
    icon: "ri-database-2-line",
    title: "资料整理",
    desc: "收录欧美成人影视作品的剧情梗概、演职员表、上映年份、片长与题材标签，形成可检索的影片档案。",
  },
  {
    icon: "ri-filter-3-line",
    title: "分类与筛选",
    desc: "按剧情、爱情、动作、悬疑、文艺、剧集等题材建立分类，并支持地区、年份与演员标签的组合筛选。",
  },
  {
    icon: "ri-search-line",
    title: "站内检索",
    desc: "支持片名、英文片名与演员姓名的模糊匹配，帮助你在最短时间内定位想了解的影片资料。",
  },
  {
    icon: "ri-link",
    title: "相关推荐",
    desc: "依据题材分类与影片标签生成推荐列表，方便顺着一条线索继续浏览风格相近的作品。",
  },
];

const FAQ = [
  {
    q: "网站是否提供影片播放或下载？",
    a: "不提供。本站为内容介绍与资讯展示站点，仅展示影片的剧情简介、演职员与分类信息，不保存、不链接任何影片文件，也不提供播放器与下载入口。",
  },
  {
    q: "影片资料如何维护？",
    a: "全部资料由编辑团队根据公开信息整理与校对，列表页展示的信息与详情页保持一致。如发现信息有误，可通过下方邮箱反馈。",
  },
  {
    q: "访问本站有年龄限制吗？",
    a: "有。本站面向 18 岁以上的成年观众，内容涉及成人影视作品介绍，请未成年人停止访问。",
  },
];

export default function AboutPage() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [errorText, setErrorText] = useState("");

  usePageMeta({
    title: "关于我们 - 欧美成人电影",
    description:
      "了解欧美成人电影网站的定位与内容范围：专注于欧美成人影视作品的资料整理与资讯展示，不保存影片资源，仅提供影片介绍、演员信息与分类检索。",
    keywords: "欧美成人电影,关于我们,影视资讯,电影介绍,免责声明",
    canonicalPath: "/about",
  });

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const data = new FormData(formEl);
    const honeypot = String(data.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("done");
      formEl.reset();
      return;
    }
    if (!email.trim() || !message.trim()) {
      setErrorText("请填写邮箱与反馈内容后再提交。");
      setStatus("error");
      return;
    }
    if (message.length > 500) {
      setErrorText("反馈内容不能超过 500 个字符。");
      setStatus("error");
      return;
    }
    setStatus("sending");
    setErrorText("");
    window.setTimeout(() => {
      setStatus("done");
      formEl.reset();
      setEmail("");
      setMessage("");
    }, 600);
  };

  return (
    <SiteLayout>
      <PageHero
        eyebrow="About This Archive"
        title="关于我们"
        subtitle="欧美成人电影是一个面向成年观众的专业影片资料站点，用清晰的结构与克制的呈现方式，帮助用户了解欧美成人影视作品的剧情、演员与分类信息。"
        breadcrumb={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
      />

      <div className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8 lg:gap-12">
          <div>
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-5 bg-accent-500 rounded-full" />
                网站定位
              </h2>
              <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                <p className="text-[14px] leading-7 text-foreground-700">
                  本站聚焦欧美地区的成人影视作品，定位为<strong className="font-semibold">资料型门户</strong>：
                  以影片为单位组织内容，围绕剧情简介、演职员信息、上映资料与题材标签，建立可持续浏览与检索的档案结构。
                  我们不追求刺激性的呈现，而是把重点放在内容可读性与信息层级上，让用户能在几次点击之内判断一部影片是否符合自己的兴趣。
                </p>
                <p className="mt-3 text-[14px] leading-7 text-foreground-600">
                  站点内容全部为文字资料与静态图片，不包含播放、下载、登录注册或支付功能。
                </p>
              </div>
            </section>

            <section className="mt-10">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-5 bg-accent-500 rounded-full" />
                内容范围
              </h2>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                {SCOPE.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
                  >
                    <span className="w-9 h-9 rounded-md bg-primary-900 text-accent-400 flex items-center justify-center">
                      <i className={item.icon} />
                    </span>
                    <h3 className="mt-3 font-heading text-[15px] font-semibold text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-foreground-600">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-10">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-5 bg-accent-500 rounded-full" />
                免责声明
              </h2>
              <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
                <ul className="space-y-2.5 text-[13.5px] leading-relaxed text-foreground-700">
                  <li className="flex gap-2">
                    <i className="ri-check-line text-accent-700 mt-0.5" />
                    本站不保存、不上传、不托管任何影片资源文件，不提供播放与下载入口。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line text-accent-700 mt-0.5" />
                    所有影片资料均为作品介绍与资讯展示用途，版权归原权利方所有。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line text-accent-700 mt-0.5" />
                    本站面向 18 岁以上成年观众，未成年人请勿访问。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line text-accent-700 mt-0.5" />
                    如权利方认为内容存在不妥，可通过下方邮箱联系，我们会在核实后及时处理。
                  </li>
                </ul>
              </div>
            </section>

            <section className="mt-10">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-5 bg-accent-500 rounded-full" />
                常见问题
              </h2>
              <div className="mt-4 flex flex-col gap-3">
                {FAQ.map((item) => (
                  <div key={item.q} className="rounded-lg border border-background-200 p-4 md:p-5">
                    <h3 className="font-heading text-[15px] font-semibold text-foreground-950">
                      {item.q}
                    </h3>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-foreground-600">{item.a}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950 flex items-center gap-2">
                <i className="ri-mail-send-line text-accent-700" />
                联系方式
              </h2>
              <p className="mt-2 text-[13px] leading-relaxed text-foreground-600">
                内容纠错、资料补充与版权沟通，请通过以下公开邮箱联系编辑部。
              </p>
              <div className="mt-4 space-y-2 text-[13px]">
                <p className="flex items-center gap-2 text-foreground-800">
                  <i className="ri-mail-line text-foreground-400" />
                  <a href="mailto:editor@eurasia-film.example" className="hover:text-accent-700 break-all">
                    editor@eurasia-film.example
                  </a>
                </p>
                <p className="flex items-center gap-2 text-foreground-800">
                  <i className="ri-time-line text-foreground-400" />
                  工作时间：周一至周五 10:00 - 18:00
                </p>
                <p className="flex items-center gap-2 text-foreground-800">
                  <i className="ri-map-pin-line text-foreground-400" />
                  内容运营地：中国 · 上海
                </p>
              </div>
            </div>

            <form
              onSubmit={submit}
              data-readdy-form
              id="about-feedback-form"
              className="rounded-lg border border-background-200 p-5"
            >
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                内容反馈
              </h2>
              <p className="mt-1 text-[12px] text-foreground-500">
                发现资料有误？留言告诉我们，编辑部会核对更新。
              </p>

              <label className="block mt-4 text-[12px] text-foreground-600" htmlFor="feedback-email">
                你的邮箱
              </label>
              <input
                id="feedback-email"
                name="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@example.com"
                className="mt-1.5 w-full h-10 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-accent-500"
              />

              <label className="block mt-3 text-[12px] text-foreground-600" htmlFor="feedback-message">
                反馈内容
              </label>
              <textarea
                id="feedback-message"
                name="message"
                rows={4}
                maxLength={500}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="请描述具体影片与需要更正的信息"
                className="mt-1.5 w-full px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-accent-500 resize-none"
              />
              <p className="mt-1 text-right text-[11px] text-foreground-400">{message.length}/500</p>

              <input
                className="cf-aux-field"
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={status === "sending"}
                className="mt-3 w-full h-10 rounded-md bg-primary-900 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-800 transition-colors cursor-pointer disabled:opacity-60"
              >
                {status === "sending" ? "提交中…" : "提交反馈"}
              </button>

              {status === "done" && (
                <p className="mt-3 text-[12px] text-accent-800 bg-accent-50 border border-accent-200 rounded-md px-3 py-2">
                  已记录你的反馈，感谢帮助我们一起校对资料。
                </p>
              )}
              {status === "error" && (
                <p className="mt-3 text-[12px] text-foreground-800 bg-background-100 border border-background-300 rounded-md px-3 py-2">
                  {errorText}
                </p>
              )}
            </form>

            <div className="rounded-lg bg-primary-900 p-5 text-background-200">
              <p className="text-[11px] tracking-[0.28em] uppercase text-accent-400 mb-2">
                Keep Exploring
              </p>
              <h3 className="font-heading text-base font-semibold text-background-50">
                继续浏览影片资料
              </h3>
              <div className="mt-3 flex flex-col gap-2">
                <Link
                  to="/category/drama"
                  className="text-[13px] text-background-300 hover:text-accent-300 transition-colors flex items-center justify-between"
                >
                  剧情类影片
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link
                  to="/category/romance"
                  className="text-[13px] text-background-300 hover:text-accent-300 transition-colors flex items-center justify-between"
                >
                  爱情类影片
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link
                  to="/search"
                  className="text-[13px] text-background-300 hover:text-accent-300 transition-colors flex items-center justify-between"
                >
                  搜索影片
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}